import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Banknote, CheckCircle, Loader2, BellRing } from 'lucide-react';
import { ChasePerformancePanel, CancellationQueue } from '@/components/ChaseOpsPanels';
import ReinstatementPanel from '@/components/ReinstatementPanel';
import { FileText } from 'lucide-react';

function StatementDownload({ isStaff, api }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [brokers, setBrokers] = useState([]);
  const [broker, setBroker] = useState('');
  useEffect(() => {
    if (isStaff) api.get('/payments/brokers').then(r => { setBrokers(r.data.brokers || []); setBroker(r.data.brokers?.[0] || ''); }).catch(() => {});
  }, [isStaff, api]);
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/payments/broker-statement?month=${month}${isStaff ? `&broker=${encodeURIComponent(broker)}` : ''}`;
  return (
    <span className="flex items-center gap-1.5" data-testid="statement-controls">
      {isStaff && (
        <select value={broker} onChange={e => setBroker(e.target.value)} data-testid="statement-broker-select"
          className="px-2 py-1.5 border rounded-sm text-[10px] bg-white max-w-[140px]" style={{ borderColor: 'rgba(136,150,168,0.3)' }}>
          {brokers.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
      )}
      <input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="statement-month"
        className="px-2 py-1 border rounded-sm text-[10px]" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
      <a href={url} target="_blank" rel="noreferrer" data-testid="download-statement"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-semibold text-white transition-opacity hover:opacity-90"
        style={{ background: C.navy }}>
        <FileText className="w-3 h-3" />Statement PDF
      </a>
    </span>
  );
}

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A' };
const fmt$ = v => `$${Number(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}`;
const STATUS_COLOR = { paid: C.green, outstanding: C.amber, partial: C.blue, overdue: C.red };
const daysOverdue = (due) => {
  if (!due) return 0;
  return Math.floor((Date.now() - new Date(due).getTime()) / 86400000);
};

export default function InvoicesPage() {
  const api = useApi();
  const { user } = useAuth();
  const isStaff = ['underwriter', 'mga_admin', 'platform_owner'].includes(user?.role);
  const isAdmin = ['mga_admin', 'platform_owner'].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [sweeping, setSweeping] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState('');

  const load = useCallback(() => {
    api.get('/payments/invoices', { params: filter ? { status: filter, limit: 50 } : { limit: 50 } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [api, filter]);
  useEffect(() => { load(); }, [load]);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const { data: res } = await api.post('/payments/reminders/run', { dry_run: false });
      toast.success(`Reminder sweep complete — ${res.sent} reminder${res.sent === 1 ? '' : 's'} sent, ${res.skipped} skipped${res.escalated_to_cancellation ? `, ${res.escalated_to_cancellation} escalated to cancellation` : ''}`);
      load();
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error('Reminder sweep failed');
    }
    setSweeping(false);
  };

  const recordReceipt = async () => {
    setPayingId(receipt.invoice_id);
    try {
      await api.post(`/payments/invoices/${receipt.invoice_id}/receipt`, {
        amount: Number(receipt.amount), method: receipt.method,
        reference: receipt.reference, received_on: receipt.received_on,
      });
      toast.success(`${fmt$(receipt.amount)} receipted against ${receipt.invoice_number}`);
      setReceipt(null); load(); setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'The receipt could not be recorded');
    }
    setPayingId(null);
  };

  if (loading) return <div data-testid="invoices-loading"><Skeleton className="h-96 rounded-sm" /></div>;
  const invoices = data?.invoices || [];

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <p className="text-xs" style={{ color: C.slate }}>
          Premium invoices — settled by bank transfer on the broker account, quoting the invoice number. Amounts include GST and stamp duty.
        </p>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1" data-testid="invoice-filter">
            {[['', 'All'], ['outstanding', 'Outstanding'], ['overdue', 'Overdue'], ['paid', 'Paid']].map(([v, label]) => (
              <button key={v || 'all'} onClick={() => { setLoading(true); setFilter(v); }}
                data-testid={`invoice-filter-${v || 'all'}`}
                className="px-2.5 py-1 rounded-sm text-[10px] font-semibold border transition-colors"
                style={filter === v
                  ? { background: C.navy, color: '#fff', borderColor: C.navy }
                  : { background: '#fff', color: C.slate, borderColor: 'rgba(136,150,168,0.3)' }}>
                {label}
              </button>
            ))}
          </span>
          <span className="text-xs font-mono" data-testid="total-outstanding" style={{ color: C.amber }}>
            Outstanding: <b>{fmt$(data?.total_outstanding)}</b>
            <span className="ml-1 text-[10px]" style={{ color: C.slate }}>across {data?.outstanding_count ?? 0} invoice{data?.outstanding_count === 1 ? '' : 's'}</span>
          </span>
          <StatementDownload isStaff={isStaff} api={api} />
          {isStaff && (
            <button onClick={runSweep} disabled={sweeping} data-testid="run-reminder-sweep"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ background: C.amber }}>
              {sweeping ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />}
              Run Overdue Chase
            </button>
          )}
        </span>
      </div>
      {isStaff && (
        <p className="text-[10px]" style={{ color: C.slate }} data-testid="auto-chase-note">
          Auto-chase runs daily: the invoice goes out the day it is raised with the reference to pay it against, then escalating reminders email the broker at <b>30</b> (friendly), <b>45</b> (firm) and <b>60</b> (final notice) days overdue. Unpaid past the final notice, the adjudicator cancels for non-payment. Premium moves on account terms — there is no card rail, so a receipt is recorded here against its bank reference.
        </p>
      )}
      {isStaff && data?.receipts && (
        <p className="text-[11px] p-2 rounded-sm" data-testid="receipt-provenance-note"
          style={{ background: 'rgba(26,76,124,0.06)', color: C.navy }}>
          <b>{fmt$(data.receipts.evidenced_total)}</b> of premium has a receipt behind it ({data.receipts.counts?.recorded_receipt || 0} invoice(s)).
          <b> {fmt$(data.receipts.assumed_total)}</b> across {data.receipts.counts?.backfill || 0} invoice(s) was inherited as paid with no receipt —
          carried and labelled rather than reset, because resetting it would start chasing and cancelling on premium that was never owed to this platform.
        </p>
      )}
      {receipt && (
        <Card className="p-3 rounded-sm border bg-white" data-testid="receipt-form" style={{ borderColor: 'rgba(29,158,117,0.4)' }}>
          <p className="text-[11px] mb-2" style={{ color: C.navy }}>
            Receipt a bank transfer against <b>{receipt.invoice_number}</b>. The reference is what makes it reconcilable against the account.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input value={receipt.amount} onChange={e => setReceipt(r => ({ ...r, amount: e.target.value }))}
              placeholder="Amount" data-testid="receipt-amount-input"
              className="px-2 py-1.5 border rounded-sm text-[11px] w-28" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
            <input value={receipt.method} onChange={e => setReceipt(r => ({ ...r, method: e.target.value }))}
              placeholder="Method" data-testid="receipt-method-input"
              className="px-2 py-1.5 border rounded-sm text-[11px] w-24" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
            <input value={receipt.reference} onChange={e => setReceipt(r => ({ ...r, reference: e.target.value }))}
              placeholder="Bank reference" data-testid="receipt-reference-input"
              className="px-2 py-1.5 border rounded-sm text-[11px] w-56" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
            <input type="date" value={receipt.received_on} onChange={e => setReceipt(r => ({ ...r, received_on: e.target.value }))}
              data-testid="receipt-date-input"
              className="px-2 py-1.5 border rounded-sm text-[11px]" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
            <button onClick={recordReceipt} disabled={!receipt.reference.trim() || !(Number(receipt.amount) > 0) || payingId === receipt.invoice_id}
              data-testid="receipt-submit"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-semibold text-white disabled:opacity-50"
              style={{ background: C.green }}>
              {payingId === receipt.invoice_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Banknote className="w-3 h-3" />}
              Record receipt
            </button>
            <button onClick={() => setReceipt(null)} data-testid="receipt-cancel" className="text-[11px]" style={{ color: C.slate }}>cancel</button>
          </div>
        </Card>
      )}
      {isStaff && <ChasePerformancePanel refreshKey={refreshKey} />}
      {isStaff && <CancellationQueue onChanged={() => { load(); setRefreshKey(k => k + 1); }} />}
      <ReinstatementPanel isStaff={isStaff} onChanged={() => { load(); setRefreshKey(k => k + 1); }} />
      {invoices.length === 0 ? (
        <Card className="p-8 rounded-sm border bg-white text-center text-sm" style={{ borderColor: 'rgba(136,150,168,0.2)', color: C.slate }}>
          No premium invoices issued yet. Invoices are raised automatically when policies bind.
        </Card>
      ) : (
        <Card className="rounded-sm border overflow-hidden" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: '#FAFBFC' }} className="border-b">
                {['Invoice', 'Business', 'Broker', 'Issued', 'Due', 'Total', 'Paid', 'Status', ''].map(h => (
                  <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => {
                const sc = STATUS_COLOR[inv.status] || C.slate;
                const payable = inv.status !== 'paid';
                return (
                  <tr key={inv.invoice_id} className="border-b hover:bg-[#FAFBFC]" style={{ borderColor: 'rgba(136,150,168,0.06)' }}
                    data-testid={`invoice-row-${inv.invoice_number}`}>
                    <td className="py-2.5 px-3 font-mono" style={{ color: C.blue }}>{inv.invoice_number}</td>
                    <td className="py-2.5 px-3 font-medium truncate max-w-[200px]" style={{ color: C.navy }}>{inv.business_name}</td>
                    <td className="py-2.5 px-3" style={{ color: C.slate }}>{inv.broker_name || '—'}</td>
                    <td className="py-2.5 px-3 font-mono" style={{ color: C.slate }}>{(inv.issued_date || '').slice(0, 10)}</td>
                    <td className="py-2.5 px-3 font-mono" style={{ color: C.slate }}>{(inv.due_date || '').slice(0, 10)}</td>
                    <td className="py-2.5 px-3 font-mono font-semibold" style={{ color: C.navy }}>{fmt$(inv.total_payable)}</td>
                    <td className="py-2.5 px-3 font-mono" style={{ color: C.green }}>{fmt$(inv.payment_received)}</td>
                    <td className="py-2.5 px-3">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase" style={{ background: sc + '12', color: sc }}>
                        {inv.status}
                      </span>
                      {payable && daysOverdue(inv.due_date) > 0 && (
                        <span className="block text-[9px] mt-0.5 font-mono" style={{ color: C.red }} data-testid={`overdue-${inv.invoice_number}`}>
                          {daysOverdue(inv.due_date)}d overdue{inv.reminders_sent ? ` · ${inv.reminders_sent} reminder${inv.reminders_sent === 1 ? '' : 's'}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      {inv.status === 'credit' ? (
                        <span className="text-[10px] font-semibold" style={{ color: C.green }}>Credit note</span>
                      ) : payable ? (
                        isAdmin ? (
                          <button onClick={() => setReceipt({ invoice_id: inv.invoice_id, invoice_number: inv.invoice_number, amount: Math.max((inv.total_payable || 0) - (inv.payment_received || 0), 0), method: 'EFT', reference: '', received_on: new Date().toISOString().slice(0, 10) })}
                            data-testid={`record-receipt-${inv.invoice_number}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-semibold text-white transition-opacity hover:opacity-90"
                            style={{ background: C.blue }}>
                            <Banknote className="w-3 h-3" />Record receipt
                          </button>
                        ) : (
                          <span className="text-[10px]" style={{ color: C.slate }}>Pay by transfer, ref {inv.invoice_number}</span>
                        )
                      ) : (
                        <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: inv.receipt_basis === 'recorded_receipt' ? C.green : C.slate }}
                          data-testid={`receipt-basis-${inv.invoice_number}`}
                          title={inv.receipt_basis === 'recorded_receipt' ? 'A receipt with a bank reference sits behind this' : 'Marked paid with no receipt behind it — inherited, not evidenced'}>
                          <CheckCircle className="w-3 h-3" />{inv.receipt_basis === 'recorded_receipt' ? 'Receipted' : 'Paid — no receipt'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
